<script setup lang="ts">
// Large Markdown pop-up (inventory F16; reference markup M1814–1911): the same pane UI inside a centred dialog.
// Shares the column's session (same draft). Backdrop mousedown outside the dialog closes it (reference). Production
// additions: role="dialog" + aria-modal, focus moved in and trapped, Escape closes, focus restored on close.
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { s } from '@/app/templateHelpers';
import MarkdownPane from './MarkdownPane.vue';
import type { MarkdownSession } from './session';
import type { MdMode, MdTaskContext, MdUser } from './format';

withDefaults(defineProps<{
  session: MarkdownSession | null;
  fileName: string | null;
  task: MdTaskContext | null;
  accent: string;
  mode: MdMode;
  histOpen: boolean;
  histView: number | null;
  userOf: (id: string) => MdUser;
  /** Reference uses ≤ 600px for the pop-up's mobile flag. */
  isMobile?: boolean;
  canEdit?: boolean;
}>(), { isMobile: false, canEdit: true });

const emit = defineEmits<{
  (e: 'update:mode', m: MdMode): void;
  (e: 'update:histOpen', v: boolean): void;
  (e: 'update:histView', n: number | null): void;
  /** Close the pop-up only (the column stays open). */
  (e: 'close'): void;
  (e: 'open-project'): void;
  (e: 'flash', msg: string): void;
}>();

const dialog = ref<HTMLElement | null>(null);
let prevFocus: HTMLElement | null = null;

const FOCUSABLE = 'button:not([disabled]), textarea, input, select, a[href], [tabindex]:not([tabindex="-1"])';
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') { e.preventDefault(); emit('close'); return; }
  if (e.key !== 'Tab' || !dialog.value) return;
  const els = [...dialog.value.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(el => el.offsetParent !== null || el === document.activeElement);
  if (!els.length) { e.preventDefault(); dialog.value.focus(); return; }
  const first = els[0]!, last = els[els.length - 1]!;
  if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog.value)) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
}
const onBackdrop = (e: MouseEvent) => { if (e.target === e.currentTarget) emit('close'); };

onMounted(async () => {
  prevFocus = document.activeElement as HTMLElement | null;
  await nextTick();
  (dialog.value?.querySelector<HTMLElement>('textarea') ?? dialog.value)?.focus();
});
onBeforeUnmount(() => { if (prevFocus && document.contains(prevFocus)) prevFocus.focus(); });
</script>

<template>
<div data-md-popup="1" @mousedown="onBackdrop" :style="s('position:fixed; inset:0; z-index:200; background:rgba(0,0,0,0.55); display:flex; align-items:center; justify-content:center; padding:max(20px, env(safe-area-inset-top)) 20px 20px;')">
  <div ref="dialog" role="dialog" aria-modal="true" :aria-label="fileName ?? undefined" tabindex="-1" data-task-card="1" @keydown="onKey" :style="s('width:min(1200px, 100%); max-height:100%; box-sizing:border-box; overflow-y:auto; scrollbar-width:none; padding:20px 24px 24px; background:var(--n1e1f22, #1e1f22); color:var(--ne7e7ea, #e7e7ea); border:1px solid var(--n34353a, #34353a); border-radius:calc(14px * var(--tp-round, 1)); box-shadow:0 24px 70px rgba(0,0,0,0.45); outline:none;')">
    <MarkdownPane in-popup :session="session" :file-name="fileName" :task="task" :accent="accent" :mode="mode" :hist-open="histOpen" :hist-view="histView" :user-of="userOf" :is-mobile="isMobile" :can-edit="canEdit"
      @update:mode="m => emit('update:mode', m)" @update:hist-open="v => emit('update:histOpen', v)" @update:hist-view="n => emit('update:histView', n)"
      @close="emit('close')" @expand="emit('close')" @open-project="emit('open-project')" @flash="m => emit('flash', m)" />
  </div>
</div>
</template>
